import type {
  CreateMemberCommunicationRequestDto,
  MemberCommunicationAttachmentDto,
  MemberCommunicationAttachmentType,
  MemberCommunicationAttachmentUploadSessionResponseDto,
  MemberCommunicationCategory,
  MemberCommunicationDto
} from "@madamuh/domain";
import { useState, type FormEvent } from "react";
import {
  isLikelyDirectUploadNetworkError,
  putFileToSignedUploadUrl
} from "../api/directUpload";
import { getProtectedApiErrorMessage } from "../session/protectedApiError";
import {
  COMMUNICATION_ATTACHMENT_LABELS,
  COMMUNICATION_CATEGORY_LABELS
} from "./MemberCommunicationCard";

const IMAGE_MAX_BYTES = 20 * 1024 * 1024;
const PDF_MAX_BYTES = 20 * 1024 * 1024;
const VIDEO_EXTENSIONS = [".mp4", ".mov", ".m4v", ".webm"] as const;

export type MemberCommunicationComposerCategory = {
  value: MemberCommunicationCategory;
  label: string;
};

export type MemberCommunicationComposerApi = {
  createCommunication: (request: CreateMemberCommunicationRequestDto) => Promise<MemberCommunicationDto>;
  createAttachment: (
    communicationId: string,
    request: {
      attachmentType: MemberCommunicationAttachmentType;
      originalFileName: string;
      mimeType: string;
      fileSizeBytes: number;
      sortOrder: number;
    }
  ) => Promise<MemberCommunicationAttachmentDto>;
  createUploadSession: (
    communicationId: string,
    attachmentId: string
  ) => Promise<MemberCommunicationAttachmentUploadSessionResponseDto>;
  completeUpload: (
    communicationId: string,
    attachmentId: string,
    request: {
      uploadSessionId: string;
      uploadSessionToken: string;
      durationSeconds?: number | null;
      width?: number | null;
      height?: number | null;
    }
  ) => Promise<MemberCommunicationDto>;
};

export type MemberCommunicationComposerValues = {
  title: string;
  body: string;
  category: MemberCommunicationCategory;
  file: File | null;
};

function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
  }
  if (bytes >= 1024) {
    return `${Math.round(bytes / 1024)}KB`;
  }
  return `${bytes}B`;
}

function inferAttachment(file: File): { attachmentType: MemberCommunicationAttachmentType; mimeType: string } | null {
  const lowerName = file.name.toLowerCase();
  if (["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
    return { attachmentType: "image", mimeType: file.type };
  }
  if (file.type === "application/pdf" || lowerName.endsWith(".pdf")) {
    return { attachmentType: "pdf", mimeType: "application/pdf" };
  }
  if (file.type.startsWith("video/") || VIDEO_EXTENSIONS.some((extension) => lowerName.endsWith(extension))) {
    if (file.type === "video/quicktime" || lowerName.endsWith(".mov")) {
      return { attachmentType: "video", mimeType: "video/quicktime" };
    }
    if (file.type === "video/webm" || lowerName.endsWith(".webm")) {
      return { attachmentType: "video", mimeType: "video/webm" };
    }
    return { attachmentType: "video", mimeType: file.type || "video/mp4" };
  }
  return null;
}

function validateFile(file: File): { attachmentType: MemberCommunicationAttachmentType; mimeType: string } {
  const inferred = inferAttachment(file);
  if (!inferred) {
    throw new Error("JPEG、PNG、WebP、動画、PDF形式のファイルを選択してください。");
  }
  if (inferred.attachmentType === "image" && file.size > IMAGE_MAX_BYTES) {
    throw new Error("画像サイズが大きすぎます。20MB以下の画像を選択してください。");
  }
  if (inferred.attachmentType === "pdf" && file.size > PDF_MAX_BYTES) {
    throw new Error("PDFサイズが大きすぎます。20MB以下のファイルを選択してください。");
  }
  return inferred;
}

function buildReadableError(error: unknown): string {
  if (isLikelyDirectUploadNetworkError(error)) {
    return "ファイルのアップロードに失敗しました。通信環境を確認して、もう一度お試しください。";
  }
  return getProtectedApiErrorMessage(error, "連絡の送信に失敗しました。もう一度お試しください。");
}

export function MemberCommunicationComposer({
  api,
  buildRequest,
  categories,
  defaultCategory = "general",
  titleLabel = "連絡タイトル（任意）",
  bodyLabel = "連絡内容（任意）",
  submitLabel = "メンバーへ連絡",
  disabled = false,
  helperText = "テキスト・画像・動画・PDFを送信できます。",
  onSuccess
}: {
  api: MemberCommunicationComposerApi;
  buildRequest: (values: MemberCommunicationComposerValues, attachmentType: MemberCommunicationAttachmentType | null) => CreateMemberCommunicationRequestDto;
  categories: readonly MemberCommunicationComposerCategory[];
  defaultCategory?: MemberCommunicationCategory;
  titleLabel?: string;
  bodyLabel?: string;
  submitLabel?: string;
  disabled?: boolean;
  helperText?: string;
  onSuccess?: (communication: MemberCommunicationDto) => Promise<void> | void;
}) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState<MemberCommunicationCategory>(defaultCategory);
  const [file, setFile] = useState<File | null>(null);
  const [sendState, setSendState] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [message, setMessage] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmedTitle = title.trim();
    const trimmedBody = body.trim();

    if (!trimmedTitle && !trimmedBody && !file) {
      setSendState("error");
      setMessage("タイトル、連絡内容、または添付ファイルを入力してください。");
      return;
    }

    let attachment: { attachmentType: MemberCommunicationAttachmentType; mimeType: string } | null = null;
    try {
      attachment = file ? validateFile(file) : null;
    } catch (error) {
      setSendState("error");
      setMessage(error instanceof Error ? error.message : "ファイルを確認してください。");
      return;
    }

    setSendState("sending");
    setMessage(null);

    try {
      const created = await api.createCommunication(
        buildRequest({ title: trimmedTitle, body: trimmedBody, category, file }, attachment?.attachmentType ?? null)
      );
      let result = created;

      if (file && attachment) {
        const createdAttachment = await api.createAttachment(created.communicationId, {
          attachmentType: attachment.attachmentType,
          originalFileName: file.name,
          mimeType: attachment.mimeType,
          fileSizeBytes: file.size,
          sortOrder: 0
        });
        const uploadSession = await api.createUploadSession(created.communicationId, createdAttachment.attachmentId);
        try {
          await putFileToSignedUploadUrl({
            uploadUrl: uploadSession.uploadUrl,
            file,
            requiredHeaders: uploadSession.requiredHeaders
          });
        } catch (error) {
          if (!isLikelyDirectUploadNetworkError(error)) {
            throw error;
          }
        }
        result = await api.completeUpload(created.communicationId, createdAttachment.attachmentId, {
          uploadSessionId: uploadSession.uploadSessionId,
          uploadSessionToken: uploadSession.uploadSessionToken
        });
      }

      setTitle("");
      setBody("");
      setFile(null);
      setCategory(defaultCategory);
      setSendState("success");
      setMessage("連絡を送信しました。");
      await onSuccess?.(result);
    } catch (error) {
      setSendState("error");
      setMessage(buildReadableError(error));
    }
  }

  const fileInfo = file ? inferAttachment(file) : null;

  return (
    <form onSubmit={handleSubmit} style={{ display: "grid", gap: 12 }}>
      <p style={{ margin: 0, fontSize: 14, opacity: 0.8 }}>{helperText}</p>
      <label style={{ display: "grid", gap: 4 }}>
        <span>{titleLabel}</span>
        <input value={title} maxLength={120} onChange={(event) => setTitle(event.currentTarget.value)} />
      </label>
      <label style={{ display: "grid", gap: 4 }}>
        <span>{bodyLabel}</span>
        <textarea value={body} maxLength={5000} rows={4} onChange={(event) => setBody(event.currentTarget.value)} />
      </label>
      <label style={{ display: "grid", gap: 4 }}>
        <span>種類</span>
        <select value={category} onChange={(event) => setCategory(event.currentTarget.value as MemberCommunicationCategory)}>
          {categories.map((item) => (
            <option key={item.value} value={item.value}>{item.label}</option>
          ))}
        </select>
      </label>
      <label style={{ display: "grid", gap: 4 }}>
        <span>添付ファイル（任意）</span>
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp,video/*,application/pdf"
          onChange={(event) => setFile(event.currentTarget.files?.[0] ?? null)}
        />
      </label>
      {file ? (
        <div style={{ fontSize: 13, opacity: 0.8 }}>
          選択中: {file.name} / {fileInfo ? COMMUNICATION_ATTACHMENT_LABELS[fileInfo.attachmentType] : "未対応"} / {formatFileSize(file.size)}
        </div>
      ) : null}
      <button type="submit" disabled={disabled || sendState === "sending"}>
        {sendState === "sending" ? "送信中..." : submitLabel}
      </button>
      {message ? (
        <p style={{ margin: 0, fontSize: 14, color: sendState === "error" ? "#b42318" : "#166534" }} role="status">
          {message}
        </p>
      ) : null}
      <p style={{ margin: 0, fontSize: 12, opacity: 0.72 }}>
        添付ありの連絡は、ファイル確認が完了してから公開されます。
      </p>
    </form>
  );
}

export const DEFAULT_COMMUNICATION_CATEGORIES: MemberCommunicationComposerCategory[] = [
  { value: "general", label: COMMUNICATION_CATEGORY_LABELS.general },
  { value: "lessonSupport", label: COMMUNICATION_CATEGORY_LABELS.lessonSupport },
  { value: "replacement", label: COMMUNICATION_CATEGORY_LABELS.replacement },
  { value: "eventSupport", label: COMMUNICATION_CATEGORY_LABELS.eventSupport }
];

export const TEACHER_OCCURRENCE_COMMUNICATION_CATEGORIES: MemberCommunicationComposerCategory[] = [
  { value: "general", label: COMMUNICATION_CATEGORY_LABELS.general },
  { value: "lessonSupport", label: COMMUNICATION_CATEGORY_LABELS.lessonSupport },
  { value: "replacement", label: COMMUNICATION_CATEGORY_LABELS.replacement }
];
